import { useEffect } from 'react'
import { authFetch } from '@/auth/authFetch'
import { API_BASE_URL } from '@/analysis/endpoints'
import { CATALOG_BRANDS, CATALOG_GAMES, CATALOG_STUDIOS } from '@/data/catalog'
import { useCatalogStore, type CatalogStreamItem } from '@/store/catalogStore'

const STREAMS_POLL_MS = 45_000

async function fetchStreams(): Promise<CatalogStreamItem[] | null> {
  // Prefer telemetry so Sidebar can show active/inventory counts.
  const withTel = await authFetch(`${API_BASE_URL}/api/catalog/streams?telemetry=1`)
  if (withTel.ok) {
    const body = await withTel.json() as { streams?: CatalogStreamItem[] }
    return body.streams ?? []
  }
  // Fall back when caller lacks stream:read or telemetry endpoint errors.
  const plain = await authFetch(`${API_BASE_URL}/api/catalog/streams`)
  if (!plain.ok) return null
  const body = await plain.json() as { streams?: CatalogStreamItem[] }
  return body.streams ?? []
}

function staticCatalogFallback() {
  return {
    games: CATALOG_GAMES.map(({ code, name }) => ({ code, name })),
    studios: CATALOG_STUDIOS.map(({ code, name, lat, lon, country }) => ({
      code, name, lat, lon, country,
    })),
    brands: CATALOG_BRANDS.map(({ code, name }) => ({ code, name })),
  }
}

/** Boot-time load of /api/catalog/* into the catalog store (source of truth for menus/filters/markers). */
export default function CatalogBootstrap() {
  const setGames = useCatalogStore((s) => s.setGames)
  const setStudios = useCatalogStore((s) => s.setStudios)
  const setBrands = useCatalogStore((s) => s.setBrands)
  const setStreams = useCatalogStore((s) => s.setStreams)
  const setLoaded = useCatalogStore((s) => s.setLoaded)

  useEffect(() => {
    let cancelled = false

    const loadStatic = async () => {
      const fallback = staticCatalogFallback()
      try {
        const [gRes, sRes, bRes] = await Promise.all([
          authFetch(`${API_BASE_URL}/api/catalog/games`),
          authFetch(`${API_BASE_URL}/api/catalog/studios`),
          authFetch(`${API_BASE_URL}/api/catalog/brands`),
        ])
        if (cancelled) return

        let games = fallback.games
        let studios = fallback.studios
        let brands = fallback.brands

        if (gRes.ok) {
          const body = await gRes.json() as { games?: typeof games }
          if (body.games?.length) games = body.games
        }
        if (sRes.ok) {
          const body = await sRes.json() as { studios?: typeof studios }
          if (body.studios?.length) studios = body.studios
        }
        if (bRes.ok) {
          const body = await bRes.json() as { brands?: typeof brands }
          if (body.brands?.length) brands = body.brands
        }

        setGames(games)
        setStudios(studios)
        setBrands(brands)
      } catch {
        if (!cancelled) {
          setGames(fallback.games)
          setStudios(fallback.studios)
          setBrands(fallback.brands)
        }
      }
    }

    const loadStreams = async () => {
      try {
        const streams = await fetchStreams()
        // Always materialize membership (empty on failure) so Include inactive
        // never silently falls back to a live-only list after loaded=true.
        if (!cancelled) setStreams(streams ?? [])
      } catch {
        if (!cancelled) setStreams([])
      }
    }

    void (async () => {
      await loadStatic()
      await loadStreams()
      if (!cancelled) setLoaded(true)
    })()

    const id = window.setInterval(() => { void loadStreams() }, STREAMS_POLL_MS)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [setGames, setStudios, setBrands, setStreams, setLoaded])

  return null
}
